import jsPDF from 'jspdf';
import autoTable, {
  type UserOptions as AutoTableOptions,
  type Styles,
} from 'jspdf-autotable';

/**
 * Thin wrapper around jsPDF for Fornitalia. Provides a pre-configured
 * document (A4 portrait, millimetres) plus helpers for the patterns we
 * use on every invoice / quote / report: a branded header, a
 * section-title line, a tabular block via autotable, and a footer
 * with page numbers.
 *
 * Keep invoice/report *templates* in their own files and import this
 * builder — don't grow this module with per-document logic.
 */

export type PdfTableRow = (string | number | null | undefined)[];

/** A header cell: a plain string, or an autotable cell def for per-cell styling
 *  (e.g. right-aligning a numeric column's header). */
export type PdfHeadCell = string | { content: string; styles?: Partial<Styles> };

export type PdfBuilder = {
  doc: jsPDF;
  /** Current y-cursor in mm from the top of the page. */
  y: number;
  /** Render the brand header at the top. Advances `y`. */
  header: (opts: { title: string; subtitle?: string }) => PdfBuilder;
  /** Render a section title (bold, slightly larger). Advances `y`. */
  sectionTitle: (text: string) => PdfBuilder;
  /** Render a simple key-value block (two columns). Advances `y`. */
  keyValues: (pairs: [label: string, value: string][]) => PdfBuilder;
  /** Render a data table via jspdf-autotable. Advances `y` to row after table. */
  table: (head: PdfHeadCell[], body: PdfTableRow[], opts?: Partial<AutoTableOptions>) => PdfBuilder;
  /** Add vertical spacing (mm). */
  space: (mm: number) => PdfBuilder;
  /**
   * Render page number footer on every page ("Página X de Y") and return
   * the underlying jsPDF doc for `.save()` / `.output()` calls.
   */
  finalize: () => jsPDF;
};

export type PdfTheme = {
  /** Brand primary (hex without leading #). */
  primary: string;
  /** Muted text (hex). */
  muted: string;
};

const DEFAULT_THEME: PdfTheme = {
  primary: '008C45', // Italian green matches the UI accent
  muted: '64748B',
};

const MARGIN_MM = 15;

export function createPdf(theme: PdfTheme = DEFAULT_THEME): PdfBuilder {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN_MM * 2;

  const builder: PdfBuilder = {
    doc,
    y: MARGIN_MM,

    header({ title, subtitle }) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(`#${theme.primary}`);
      doc.text(title, MARGIN_MM, builder.y + 4);

      if (subtitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(`#${theme.muted}`);
        doc.text(subtitle, MARGIN_MM, builder.y + 10);
      }

      // Separator line.
      doc.setDrawColor(220);
      doc.setLineWidth(0.2);
      doc.line(MARGIN_MM, builder.y + 14, MARGIN_MM + contentWidth, builder.y + 14);

      builder.y += 20;
      return builder;
    },

    sectionTitle(text) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(20);
      doc.text(text, MARGIN_MM, builder.y);
      builder.y += 6;
      return builder;
    },

    keyValues(pairs) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(20);
      const labelWidth = 45;
      for (const [label, value] of pairs) {
        doc.setTextColor(`#${theme.muted}`);
        doc.text(label, MARGIN_MM, builder.y);
        doc.setTextColor(20);
        doc.text(value || '—', MARGIN_MM + labelWidth, builder.y);
        builder.y += 5;
      }
      builder.y += 2;
      return builder;
    },

    table(head, body, opts) {
      autoTable(doc, {
        startY: builder.y,
        head: [head],
        body: body.map((row) => row.map((c) => (c == null ? '' : String(c)))),
        margin: { left: MARGIN_MM, right: MARGIN_MM },
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: `#${theme.primary}`, textColor: 255, halign: 'left' },
        alternateRowStyles: { fillColor: [245, 247, 249] },
        ...opts,
      });
      // jspdf-autotable attaches the last cursor position to `doc.lastAutoTable`.
      const lastY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY;
      builder.y = (lastY ?? builder.y) + 6;
      return builder;
    },

    space(mm) {
      builder.y += mm;
      return builder;
    },

    finalize() {
      const total = doc.getNumberOfPages();
      for (let i = 1; i <= total; i += 1) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(`#${theme.muted}`);
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.text(
          `Página ${i} de ${total}`,
          pageWidth - MARGIN_MM,
          pageHeight - 8,
          { align: 'right' },
        );
      }
      return doc;
    },
  };

  return builder;
}

/** Trigger a browser download with the given filename. */
export function downloadPdf(doc: jsPDF, filename: string): void {
  const safe = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(safe);
}

/** Return a Blob URL for previewing the PDF inside an iframe. */
export function pdfObjectUrl(doc: jsPDF): string {
  return doc.output('bloburl').toString();
}
